import React, { useState, useEffect } from 'react';
import { LinkedParentInfo, getLinkedParents, removeLinkedParent } from '../../services/auth';
import { useAuth } from '../../contexts/AuthContext';

export const LinkedParentsSettings: React.FC = () => {
  const { currentCurriculum } = useAuth();
  const [parents, setParents] = useState<LinkedParentInfo[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  useEffect(() => {
    loadParents();
  }, [currentCurriculum]); // Refresh when curriculum changes

  const loadParents = async () => {
    try {
      const data = await getLinkedParents();
      setParents(data);
    } catch {
      setError('Failed to load linked parents');
    } finally {
      setLoading(false);
    }
  };

  const handleRemove = async (linkId: string, parentName: string | null) => {
    const name = parentName || 'this parent';
    if (!window.confirm(`Remove ${name}'s access? They will no longer be able to follow your progress.`)) {
      return;
    }

    try {
      await removeLinkedParent(linkId);
      setParents(parents.filter((p) => p.id !== linkId));
    } catch {
      setError('Failed to remove parent access');
    }
  };

  if (loading) {
    return <div className="panel-loading">Loading...</div>;
  }

  if (error) {
    return <div className="panel-error">{error}</div>;
  }

  return (
    <div className="linked-parents-settings">
      <h3>Linked Parents</h3>
      {parents.length === 0 ? (
        <div className="no-parents-card">
          <p className="no-parents-text">No parents linked to your account.</p>
          <p className="no-parents-hint">
            A parent can request to follow your progress from their dashboard. You'll always get to approve it first.
          </p>
        </div>
      ) : (
        <div className="linked-parents-list">
          {parents.map((parent) => (
            <div key={parent.id} className="linked-parent-item">
              <div className="linked-parent-info">
                <span className="linked-parent-name">
                  {parent.parent_name || 'Parent'}
                  {parent.parent_email && (
                    <span className="linked-parent-email"> ({parent.parent_email})</span>
                  )}
                </span>
                {parent.created_at && (
                  <span className="linked-parent-since">
                    Following your progress since {new Date(parent.created_at).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })}
                  </span>
                )}
                <span className="linked-parent-detail">
                  What they can see: your practice activity, scores, and areas where you might need help.
                </span>
              </div>
              <button
                className="remove-parent-btn"
                onClick={() => handleRemove(parent.id, parent.parent_name)}
              >
                Remove Access
              </button>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};
